import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { licenseRequestsListResponseSchema, type LicenseRequest } from '@/schemas/license-requests';
import { RequestsQueueTable } from '@/components/app/requests-queue-table';
import { RequestReviewSheet } from '@/components/app/request-review-sheet';

const STATUSES = ['submitted', 'approved', 'rejected'] as const;

function buildEndpoint(searchParams: URLSearchParams): string {
  const p = new URLSearchParams();
  p.set('page', searchParams.get('page') ?? '1');
  p.set('limit', searchParams.get('limit') ?? '20');
  const status = searchParams.get('status');
  if (status) p.set('status', status);
  if (searchParams.get('offline') === '1') p.set('offlineRequested', 'true');
  return `/license-requests?${p.toString()}`;
}

function TableSkeleton() {
  return (
    <div className="space-y-2 pt-4">
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={`h-${i}`} className="h-9 flex-1" />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, ri) => (
        <div key={ri} className="flex gap-2">
          {Array.from({ length: 6 }).map((_, ci) => (
            <Skeleton key={`${ri}-${ci}`} className="h-11 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Review queue for developer_operator / admin — all license requests (#74). */
export function RequestsQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<LicenseRequest | null>(null);

  const endpoint = useMemo(() => buildEndpoint(searchParams), [searchParams]);

  const query = useQuery({
    queryKey: ['license-requests', 'queue', endpoint],
    queryFn: async () => {
      const resp = await apiFetch(endpoint);
      return licenseRequestsListResponseSchema.parse(await resp.json());
    },
    staleTime: 15_000,
  });

  // Work queue first: submitted requests on top, the rest keeps server order (createdAt DESC).
  const rows = useMemo(() => {
    const data = query.data?.data ?? [];
    return [...data].sort(
      (a, b) => Number(b.status === 'submitted') - Number(a.status === 'submitted')
    );
  }, [query.data]);

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const limit = Math.max(1, parseInt(searchParams.get('limit') ?? '20', 10));
  const status = searchParams.get('status') ?? '';
  const offlineOnly = searchParams.get('offline') === '1';

  const patchParams = (patch: (p: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      patch(p);
      return p;
    });
  };

  const setPage = (next: number) => patchParams((p) => p.set('page', String(next)));
  const setLimit = (next: number) =>
    patchParams((p) => {
      p.set('limit', String(next));
      p.set('page', '1');
    });

  const data = query.data;
  const isFirstLoad = query.isLoading && !data;
  const isRefetching = query.isFetching && !query.isLoading;
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const hasFilters = status.length > 0 || offlineOnly;

  const clearFilters = () =>
    patchParams((p) => {
      p.delete('status');
      p.delete('offline');
      p.set('page', '1');
    });

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Page head */}
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('requests.queue.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('requests.queue.subtitle')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="requests-status-filter" className="text-xs text-faint">
            {t('requests.queue.status')}
          </Label>
          <Select
            value={status || 'all'}
            onValueChange={(v) =>
              patchParams((p) => {
                if (v === 'all') p.delete('status');
                else p.set('status', v);
                p.set('page', '1');
              })
            }
          >
            <SelectTrigger
              id="requests-status-filter"
              className="h-9 w-[170px] border-input bg-transparent text-[13.5px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('requests.queue.statusAll')}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`requests.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex h-9 items-center gap-2">
          <Switch
            id="requests-offline-filter"
            checked={offlineOnly}
            onCheckedChange={(checked) =>
              patchParams((p) => {
                if (checked) p.set('offline', '1');
                else p.delete('offline');
                p.set('page', '1');
              })
            }
          />
          <Label htmlFor="requests-offline-filter" className="text-[13.5px] text-muted-foreground">
            {t('requests.queue.offlineOnly')}
          </Label>
        </div>
      </div>

      {/* Content */}
      {query.isError ? (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{t('requests.queue.listError')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : isFirstLoad ? (
        <TableSkeleton />
      ) : total === 0 ? (
        hasFilters ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-[15px] text-muted-foreground">{t('requests.queue.noMatch')}</p>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {t('common.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
            <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
              {t('requests.queue.emptyTitle')}
            </h2>
            <p className="max-w-[42ch] text-center text-[14.5px] text-muted-foreground">
              {t('requests.queue.emptyHint')}
            </p>
          </div>
        )
      ) : (
        <div
          className={`transition-opacity ${isRefetching ? 'pointer-events-none opacity-60' : ''}`}
        >
          <RequestsQueueTable data={rows} onSelect={setSelected} />
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <span className="text-[13px] tabular-nums text-faint">
            {t('common.showingRange', { start, end, total })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              aria-label={t('common.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[64px] text-center text-[13px] tabular-nums text-muted-foreground">
              {page} / {pages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => setPage(page + 1)}
              disabled={page >= pages}
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
              <SelectTrigger
                className="h-8 w-[72px] border-input bg-transparent text-[13px]"
                aria-label={t('common.pageSize')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <RequestReviewSheet
        request={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
